/**
 * County hub data — one entry per served county.
 *
 * Only facts already asserted elsewhere in the project (service area,
 * licensing, packages, service pillars). Do NOT invent stats or claims.
 */
import type { LucideIcon } from "lucide-react";
import { Building2, Palmtree, Sun, Mountain, Waves } from "lucide-react";

export interface AduLocalResource {
  label: string;
  url: string;
  agency: string;
}

/**
 * County-specific ADU section (Phase 3). Each entry must contain
 * genuinely unique copy — the CountyHub renderer relies on this to
 * avoid doorway/duplication patterns.
 */
export interface AduLocalSection {
  /** Short county-specific H2. */
  heading: string;
  /** 2-3 sentence intro; unique per county. */
  intro: string;
  /** Plain-English jurisdiction note (incorporated cities vs county). */
  jurisdictionNote: string;
  /** 3-5 unique local considerations. */
  considerations: { title: string; body: string }[];
  /** Official jurisdictional resources for this county. */
  resources: AduLocalResource[];
  /** Preselected intake CTA source slug (query string suffix). */
  ctaSource: string;
}

export interface CountyHub {
  slug: string;
  /** Matches COUNTIES[].name in src/data/serviceAreas.ts */
  countyName: string;
  displayName: string;
  icon: LucideIcon;
  /** One-line hero subhead. */
  tagline: string;
  /** 2-3 sentence intro; only facts we can back up. */
  intro: string;
  /** Landmark cities in this county we already list — used for internal links. */
  featuredCities: string[];
  /** Local considerations we address (fire zone, coastal salt air, etc.). */
  considerations: { title: string; body: string }[];
  /** Optional package that's a natural fit for this county. */
  featuredPackageSlug?: "resilient-home" | "electric-home" | "restoration";
  /** County-specific ADU local section (Phase 3). */
  aduLocal: AduLocalSection;
  seo: { title: string; description: string };
}

const HCD_RESOURCE: AduLocalResource = {
  label: "California HCD — ADU guidance",
  url: "https://www.hcd.ca.gov/building-standards/adu",
  agency: "California Department of Housing & Community Development",
};

export const COUNTY_HUBS: CountyHub[] = [
  {
    slug: "los-angeles",
    countyName: "Los Angeles",
    displayName: "Los Angeles County",
    icon: Building2,
    tagline:
      "Construction, ADUs, solar, roofing and electrical under one licensed contractor — from Pasadena and the SGV to the Westside, South Bay, and the Antelope Valley.",
    intro:
      "Los Angeles County is our home base — our office sits in Pasadena at the edge of the San Gabriel Valley, and our published service area spans the coast to the high desert. We manage ADUs, additions, remodels, and new construction as a licensed general contractor (CSLB #999485, B · C-10 · C-39), and coordinate solar, roofing, EV chargers, and electrical scope on the same project.",
    featuredCities: [
      "Pasadena", "Los Angeles", "Long Beach", "Glendale", "Burbank",
      "Santa Monica", "Torrance", "Santa Clarita", "Palmdale",
    ],
    considerations: [
      {
        title: "Fire-zone permitting on the hillsides",
        body: "Altadena, La Cañada, Malibu, Topanga and the Angeles-front foothills bring stricter inspection and Class A roofing requirements. We pull the permits and coordinate the sign-offs.",
      },
      {
        title: "LADWP, SCE, and PWP all in one county",
        body: "Utility interconnection paperwork differs between LADWP, SCE, Pasadena, Burbank, and Glendale Water & Power. We handle each utility's process directly.",
      },
      {
        title: "Tile, shingle and flat-roof mix",
        body: "LA's housing stock swings from mid-century flat roofs to full concrete-tile Spanish. Our roofing crews are set up for all three — including tile reset before a re-roof or solar install.",
      },
    ],
    featuredPackageSlug: "resilient-home",
    aduLocal: {
      heading: "ADU construction in Los Angeles County",
      intro:
        "Los Angeles County spans the City of Los Angeles, 87 other incorporated cities, and large unincorporated areas — each with its own planning counter, its own zoning, and its own review cadence. Magic Builders manages garage-conversion and detached-backyard ADU projects across the county and coordinates the design professionals and agency reviews required for your specific parcel.",
      jurisdictionNote:
        "Inside the City of Los Angeles, LA City Planning and LADBS govern ADU review. Inside another incorporated city (Pasadena, Long Beach, Santa Monica, Glendale, Burbank, etc.), that city's planning department governs. Unincorporated LA County parcels are reviewed by LA County Regional Planning and LA County Public Works.",
      considerations: [
        {
          title: "Existing-structure condition on garage conversions",
          body: "Older detached garages often need foundation, framing, moisture, or roof repair before they can host habitable space. We scope the structural condition up front so the ADU budget isn't ambushed later.",
        },
        {
          title: "Hillside and site access",
          body: "Hillside lots in the SGV foothills, Westside canyons, and Santa Monica Mountains bring grading, retaining, and access-route review that flat-lot ADUs don't. Delivery of concrete, framing, and modular components has to be planned around access.",
        },
        {
          title: "Sewer, water, and utility routing",
          body: "Detached ADUs typically need new sewer, water, gas, and electrical runs from the main house or the street. Depending on the parcel and agency, a septic capacity evaluation or a sewer connection may be required.",
        },
        {
          title: "Coastal and fire-zone review",
          body: "Coastal-zone parcels (Malibu, coastal Santa Monica, Palos Verdes) may need Coastal Commission or local coastal permit review; foothill parcels in Very High Fire Hazard Severity Zones bring Class A roof, ember-resistant assemblies, and defensible-space scope.",
        },
      ],
      resources: [
        HCD_RESOURCE,
        {
          label: "LA County Planning — ADU program",
          url: "https://planning.lacounty.gov/planning-permits/accessory-dwelling-units/",
          agency: "Los Angeles County Regional Planning",
        },
        {
          label: "City of Los Angeles — adopted land-use regulations",
          url: "https://planning.lacity.gov/plans-policies/adopted-land-use-regulations",
          agency: "Los Angeles City Planning",
        },
      ],
      ctaSource: "county-los-angeles",
    },
    seo: {
      title: "Los Angeles County ADUs, Construction & Solar | Magic Builders",
      description:
        "Licensed contractor for ADUs, additions, remodels, solar, roofing and electrical across Los Angeles County — Pasadena, Long Beach, SGV, South Bay and Antelope Valley. CSLB #999485.",
    },
  },
  {
    slug: "orange",
    countyName: "Orange",
    displayName: "Orange County",
    icon: Palmtree,
    tagline:
      "One licensed team for ADUs, construction, solar, roofing, and electrical across Orange County — from Newport and Irvine down to San Clemente, and north to Anaheim and Fullerton.",
    intro:
      "Our published Orange County service area spans coastal Newport, Laguna, and Dana Point through the master-planned inland communities of Irvine, Mission Viejo, and Rancho Santa Margarita, up to Anaheim and Yorba Linda. Construction, ADU, roofing, solar, battery, EV, and electrical scope are managed by one licensed general contractor (CSLB #999485), with qualified trade partners coordinated as needed.",
    featuredCities: [
      "Irvine", "Newport Beach", "Huntington Beach", "Anaheim",
      "Santa Ana", "Mission Viejo", "Costa Mesa", "Fullerton",
      "San Clemente",
    ],
    considerations: [
      {
        title: "HOA and coastal design review",
        body: "Most OC master-planned communities require ARC/HOA sign-off on solar layout, roof material, and equipment placement. We package the submittal materials so approvals don't stall the project.",
      },
      {
        title: "Coastal salt-air on equipment and roofing",
        body: "Homes within a few miles of the coast benefit from corrosion-rated racking, stainless flashings, and equipment placement out of prevailing salt spray. Our design accounts for it.",
      },
      {
        title: "SCE interconnection under NEM 3.0",
        body: "Almost all of Orange County sits on Southern California Edison. We size solar + battery scope with NEM 3.0 export economics in mind and file the interconnection directly.",
      },
    ],
    featuredPackageSlug: "electric-home",
    aduLocal: {
      heading: "ADU construction in Orange County",
      intro:
        "Orange County ADU work splits between the 34 incorporated cities and the unincorporated county pockets. Cities each run their own planning process, and many master-planned communities layer a separate HOA/ARC review on top. Magic Builders coordinates the construction scope with the design professionals and agencies your parcel actually reports to.",
      jurisdictionNote:
        "In an incorporated city (Irvine, Newport Beach, Anaheim, Mission Viejo, Costa Mesa, etc.), that city's planning department governs ADU review. Unincorporated Orange County parcels are reviewed by OC Public Works. HOA/ARC approval, when required, is a separate private process from any government permit.",
      considerations: [
        {
          title: "HOA and ARC review is a separate private process",
          body: "Many master-planned communities require ARC sign-off on ADU siting, roof and stucco color, window placement, and equipment locations before a permit set is even accepted. Plan for it as its own review track.",
        },
        {
          title: "Site access and utility routing on tight lots",
          body: "Interior Irvine, Newport, and inland OC lots are often tight for backhoe access. Sewer laterals, gas, and electrical routing to a detached ADU need to be planned early so a fence or hardscape isn't rebuilt twice.",
        },
        {
          title: "Coastal-zone parcels",
          body: "Parcels seaward of the Coastal Zone boundary can require additional coastal review. Coastal municipalities (Newport, Laguna, Dana Point, San Clemente) each administer this differently.",
        },
        {
          title: "Pre-approved plans only where they apply",
          body: "Orange County Public Works has published pre-approved ADU plans for unincorporated parcels. They can save time when the parcel qualifies, but they don't apply inside incorporated cities and don't guarantee approval — the plan still has to fit the site.",
        },
      ],
      resources: [
        HCD_RESOURCE,
        {
          label: "OC Public Works — pre-approved ADU plans",
          url: "https://pw.oc.gov/news/pre-approved-accessory-dwelling-unit-adu-plans-provide-streamlined-and-affordable-option",
          agency: "Orange County Public Works",
        },
      ],
      ctaSource: "county-orange",
    },
    seo: {
      title: "Orange County ADUs, Construction & Solar | Magic Builders",
      description:
        "Licensed contractor for ADUs, additions, remodels, solar, roofing and electrical across Orange County — Irvine, Newport Beach, Anaheim, Mission Viejo, San Clemente. CSLB #999485.",
    },
  },
  {
    slug: "riverside",
    countyName: "Riverside",
    displayName: "Riverside County",
    icon: Sun,
    tagline:
      "ADUs, construction, solar + battery, roofing, and electrical across the Inland Empire and Coachella Valley — one licensed team, one project manager.",
    intro:
      "Riverside County is a strong solar and battery market — long hot summers, heavy AC load, and desert cities where backup storage can be the difference between waiting out an outage in comfort or in the dark. Our published service area covers Corona and Eastvale in the northwest, the Menifee/Temecula corridor, Riverside/Moreno Valley, and the desert cities from Palm Springs to Indio.",
    featuredCities: [
      "Riverside", "Corona", "Temecula", "Murrieta", "Moreno Valley",
      "Menifee", "Eastvale", "Palm Desert", "Palm Springs", "La Quinta",
    ],
    considerations: [
      {
        title: "Cooling-driven bills reward battery backup",
        body: "Riverside County homes run hard on AC from June through October. A right-sized solar + battery system can flatten peak-rate demand and provide backup capacity for essential loads during utility outages.",
      },
      {
        title: "Tile and clay roofs are the norm",
        body: "Most Riverside County homes are on concrete or clay tile. We tile-reset before solar so mounts land in structure, not in cracked tile — and we warranty the roofing and the array together.",
      },
      {
        title: "SCE + IID interconnection",
        body: "Most of the county is Southern California Edison; the Coachella end sits on Imperial Irrigation District. We handle both interconnection processes directly.",
      },
    ],
    featuredPackageSlug: "resilient-home",
    aduLocal: {
      heading: "ADU construction in Riverside County",
      intro:
        "Riverside County ADU work splits between the City of Riverside, other incorporated cities (Corona, Temecula, Murrieta, Palm Desert and the Coachella Valley cities), and the unincorporated county — which includes wine country, foothills, and desert parcels. Magic Builders coordinates ADU construction and the associated design professionals with the appropriate city or county reviewer.",
      jurisdictionNote:
        "City of Riverside ADU applications are reviewed by City of Riverside CEDD. Other incorporated cities each administer their own program. Unincorporated Riverside County parcels are reviewed by Riverside County Planning; applicable California state ADU law and the current Riverside County ordinance (including the county's Additional Residential Accommodations / ARA framework) govern — confirm the current adopted rules with the county before design.",
      considerations: [
        {
          title: "Garage and accessory-structure conversions",
          body: "Older ranch and hillside homes across Corona, Riverside, and the inland cities have detached garages that can convert to ADUs, but foundation, framing, and slab moisture usually need to be assessed before scope is priced.",
        },
        {
          title: "Multigenerational living patterns",
          body: "Detached backyard ADUs are common as multigenerational housing across Menifee, Moreno Valley, Corona, and Eastvale. Site plan, privacy, and separate utility metering come up early.",
        },
        {
          title: "Sewer vs septic on outer parcels",
          body: "Foothill and rural parcels can be on septic, which changes how a new ADU is added — either connecting to an expanded septic system or trenching to municipal sewer where it's reachable.",
        },
        {
          title: "Climate-appropriate envelope and mechanical",
          body: "Coachella Valley and inland-Riverside ADUs need real thought about wall assembly, glazing, and mechanical sizing so the ADU can be cooled affordably in summer and comfortable in winter.",
        },
      ],
      resources: [
        HCD_RESOURCE,
        {
          label: "Riverside County — Additional Residential Accommodations (ARA)",
          url: "https://planning.rctlma.org/riverside-county-additional-residential-accommodations-ara",
          agency: "Riverside County Planning",
        },
        {
          label: "City of Riverside — ADU program",
          url: "https://riversideca.gov/cedd/planning/development-related/adusjadus",
          agency: "City of Riverside CEDD",
        },
        {
          label: "City of Riverside — permit-ready ADU plans",
          url: "https://riversideca.gov/cedd/building-safety/special-programs/permit-ready-adu-plans",
          agency: "City of Riverside CEDD",
        },
      ],
      ctaSource: "county-riverside",
    },
    seo: {
      title: "Riverside County ADUs, Construction & Solar | Magic Builders",
      description:
        "Licensed contractor for ADUs, solar, battery, roofing and electrical across Riverside County — Corona, Temecula, Menifee, Moreno Valley and Coachella Valley. CSLB #999485.",
    },
  },
  {
    slug: "san-bernardino",
    countyName: "San Bernardino",
    displayName: "San Bernardino County",
    icon: Mountain,
    tagline:
      "ADUs, construction, solar, roofing, and electrical across the Inland Empire, high desert, and mountain communities — one licensed team from Rancho Cucamonga to Big Bear.",
    intro:
      "San Bernardino County is the largest county in the country by area, and we work it end-to-end — from the Inland Empire cities of Rancho Cucamonga, Ontario, Fontana, and Redlands, up into the mountain communities of Lake Arrowhead and Big Bear, and out to the high-desert cities of Victorville, Hesperia, and Apple Valley. One CSLB license (#999485, B · C-10 · C-39) covers construction, ADUs, solar, roofing, and electrical across all of it.",
    featuredCities: [
      "Rancho Cucamonga", "Ontario", "Fontana", "San Bernardino",
      "Redlands", "Chino Hills", "Victorville", "Hesperia",
      "Big Bear Lake", "Lake Arrowhead",
    ],
    considerations: [
      {
        title: "Mountain snow load and fire zone",
        body: "Big Bear, Lake Arrowhead, Crestline and the ridge communities need snow-rated racking and Class A fire-rated roofing. We design and permit for it.",
      },
      {
        title: "High-desert heat and wind",
        body: "Victor Valley and the Morongo Basin see extreme summer heat and sustained wind. Racking uplift ratings, module derate, and battery placement all matter — we spec for it.",
      },
      {
        title: "Wildfire hardening in foothill zones",
        body: "Many county foothill and mountain parcels sit inside CAL FIRE Very High Fire Hazard Severity Zones. We coordinate wildfire sprinkler, roof, and defensible-space work.",
      },
    ],
    featuredPackageSlug: "resilient-home",
    aduLocal: {
      heading: "ADU construction in San Bernardino County",
      intro:
        "San Bernardino County ADUs sit in three very different environments: the Inland Empire valley floor, the mountain communities, and the high desert. Magic Builders coordinates ADU construction and the associated design professionals with the appropriate city or county reviewer, and designs the envelope and mechanical scope for the environment the ADU is actually sited in.",
      jurisdictionNote:
        "Incorporated cities (Rancho Cucamonga, Ontario, Fontana, Redlands, Chino Hills, Victorville, Hesperia, etc.) each run their own ADU review. Unincorporated San Bernardino County parcels — including many mountain and desert areas — are reviewed by San Bernardino County Land Use Services (Building & Safety, Planning).",
      considerations: [
        {
          title: "Inland Empire vs high desert vs mountain",
          body: "A detached ADU in Rancho Cucamonga, a garage conversion in Victorville, and a mountain ADU near Lake Arrowhead are three different projects. Snow load, wind uplift, and wildland-urban-interface assemblies all change.",
        },
        {
          title: "Septic and sewer availability",
          body: "Outer county parcels may be on septic. Adding an ADU can trigger a septic-system evaluation and, sometimes, upgrade or replacement before habitable occupancy is allowed.",
        },
        {
          title: "Easements, access, and grading",
          body: "Large lots often carry access easements, drainage requirements, and grading limits that shape where a detached ADU can actually sit. We work through it before design is finalized.",
        },
        {
          title: "Wind, snow, and fire review",
          body: "High-desert and mountain parcels bring wind uplift and snow-load engineering; foothill and mountain parcels in Very High Fire Hazard Severity Zones trigger ember-resistant assemblies and defensible-space scope.",
        },
      ],
      resources: [
        HCD_RESOURCE,
        {
          label: "San Bernardino County — ADU handout",
          url: "https://lus.sbcounty.gov/wp-content/uploads/sites/48/ADU-Handout-May2026.pdf",
          agency: "San Bernardino County Land Use Services",
        },
        {
          label: "San Bernardino County — Building & Safety",
          url: "https://lus.sbcounty.gov/building-safety-home/",
          agency: "San Bernardino County Land Use Services",
        },
      ],
      ctaSource: "county-san-bernardino",
    },
    seo: {
      title: "San Bernardino County ADUs, Construction & Solar | Magic Builders",
      description:
        "Licensed contractor for ADUs, additions, solar, roofing and electrical across San Bernardino County — Rancho Cucamonga, Ontario, Redlands, Victorville, Big Bear. CSLB #999485.",
    },
  },
  {
    slug: "ventura",
    countyName: "Ventura",
    displayName: "Ventura County",
    icon: Waves,
    tagline:
      "ADUs, construction, solar, roofing, and electrical from Thousand Oaks and Simi Valley through Camarillo, Oxnard, and coastal Ventura.",
    intro:
      "Ventura County work runs across the whole county — Simi Valley and Thousand Oaks in the east, Camarillo and Moorpark through the middle, the Oxnard plain, and the coastal cities of Ventura and Ojai. Wildfire-defense scope is a real conversation in the hillside neighborhoods, and we handle it the same way we handle the rest of a project — under one licensed general contractor.",
    featuredCities: [
      "Thousand Oaks", "Simi Valley", "Camarillo", "Oxnard",
      "Ventura", "Moorpark", "Ojai",
    ],
    considerations: [
      {
        title: "Very High Fire Hazard Severity Zones",
        body: "Ojai, the Santa Monica Mountains foothills, and stretches of Simi and Thousand Oaks sit inside CAL FIRE VHFHSZ. Roof class, ember-resistant assemblies, and defensible-space scope all come into play — we handle the design and permitting.",
      },
      {
        title: "SCE interconnection under NEM 3.0",
        body: "Ventura County is almost entirely Southern California Edison. We size solar and battery scope with NEM 3.0 export economics in mind and file interconnection directly.",
      },
      {
        title: "Coastal salt-air and marine layer",
        body: "Homes along the coast (Oxnard, Ventura, Port Hueneme) benefit from corrosion-rated racking and stainless flashings. We spec accordingly.",
      },
    ],
    featuredPackageSlug: "resilient-home",
    aduLocal: {
      heading: "ADU construction in Ventura County",
      intro:
        "Ventura County ADU work splits between the ten incorporated cities and the unincorporated county — which spans coastal terraces, the Ojai Valley, ranchland, and Santa Monica Mountains foothills. Magic Builders coordinates ADU construction and the associated design professionals with the reviewing city or Ventura County RMA.",
      jurisdictionNote:
        "Incorporated cities (Thousand Oaks, Simi Valley, Camarillo, Oxnard, Ventura, Moorpark, Ojai) each run their own ADU review — Newbury Park is a community within the City of Thousand Oaks, not a separate incorporated city. Unincorporated Ventura County parcels are reviewed by the Ventura County Resource Management Agency (RMA).",
      considerations: [
        {
          title: "Coastal vs non-coastal review",
          body: "Parcels within the Coastal Zone (parts of Oxnard, Ventura, and unincorporated coastal county) can require Coastal Commission or local coastal permit review in addition to standard building review.",
        },
        {
          title: "Hillside and canyon access",
          body: "Ojai Valley, north Thousand Oaks, and Santa Monica Mountains parcels bring hillside access, grading, and drainage review that flat-lot Camarillo or Oxnard ADUs don't.",
        },
        {
          title: "Fire-area assemblies and defensible space",
          body: "Ojai, hillside Ventura, and Santa Monica Mountains parcels in Very High Fire Hazard Severity Zones require Class A roof assemblies, ember-resistant vents, and defensible-space planning around the ADU.",
        },
        {
          title: "Utility routing and grading",
          body: "Detached ADUs on larger county parcels often need new sewer or septic, water, gas, and electrical runs of meaningful length — planned early so grading and hardscape are only done once.",
        },
      ],
      resources: [
        HCD_RESOURCE,
        {
          label: "Ventura County RMA — Accessory Dwelling Units",
          url: "https://rma.venturacounty.gov/divisions/planning/accessory-dwelling-units/",
          agency: "Ventura County Resource Management Agency",
        },
      ],
      ctaSource: "county-ventura",
    },
    seo: {
      title: "Ventura County ADUs, Construction & Solar | Magic Builders",
      description:
        "Licensed contractor for ADUs, additions, solar, roofing and electrical across Ventura County — Thousand Oaks, Simi Valley, Camarillo, Oxnard, Ventura, Ojai. CSLB #999485.",
    },
  },
];

export function getCountyHub(slug: string): CountyHub | undefined {
  return COUNTY_HUBS.find((c) => c.slug === slug);
}

/**
 * Look up a county hub by the human-readable county name that appears on
 * a CityData row (e.g. "Los Angeles" → the los-angeles hub). Used by the
 * dynamic city/service pages to canonicalize non-unique combinations
 * back to the county pillar.
 */
export function getCountyHubByName(countyName: string): CountyHub | undefined {
  const target = countyName.trim().toLowerCase();
  return COUNTY_HUBS.find((c) => c.countyName.toLowerCase() === target);
}
